/**
 * Person profile, Call Log sheet, Schedule Visit sheet.
 *
 * §12 Person retains: name, case identifier, phone, Call, Schedule, Note,
 * location, shared-location count, unresolved warning, next visit, team,
 * recent activity.
 * §13 Call Log outcomes and follow-up behaviour.
 * §14 Schedule Visit fields, durations and team assignment.
 */

import { useState } from 'react';

// ---------------------------------------------------------------------------
// Person (§12)
// ---------------------------------------------------------------------------

export interface ActivityEntry {
  id: string;
  kind: string;
  label: string;
  whenLabel: string;
}

export interface PersonDetail {
  id: string;
  name: string;
  caseId: string;
  phone: string | null;
  addressLine: string;
  cityLine: string;
  /** Other authorized records at the same physical stop. §19 */
  sharedLocationCount: number;
  /** Set when the address could not be resolved. §20 */
  unresolvedWarning: string | null;
  nextVisitLabel: string | null;
  teamName: string | null;
  doNotVisit: boolean;
  caseStatus: string | null;
  caseRecordId: string | null;
  cases: Array<{ id: string; caseNumber: string; status: string; teamName: string | null }>;
  statusHistory: Array<{ previous: string | null; next: string; source: string; at: string }>;
  activity: ActivityEntry[];
}

export function PersonScreen({
  person, onCall, onLogCall, onSchedule, onNote, onSubmitAddress, onReassign, onBack, backLabel = 'People',
}: {
  person: PersonDetail | null;
  onCall: () => void;
  onLogCall: () => void;
  onSchedule: () => void;
  onNote: () => void;
  onSubmitAddress: () => void;
  onReassign?: () => void;
  onBack: () => void;
  backLabel?: string;
}) {
  if (!person) {
    return <p className="pt-8 text-base text-neutral-700">Loading…</p>;
  }

  return (
    <div className="pt-4">
      <button className="btn btn-ghost mb-2 -ml-4" onClick={onBack}>← {backLabel}</button>

      <h1 className="mb-1">{person.name}</h1>
      <p className="text-base text-neutral-700 mb-4">
        {person.caseId}
        {person.phone && ` · ${person.phone}`}
      </p>
      {person.doNotVisit && (
        <p className="mb-4 p-3 bg-neutral-200 text-neutral-800 rounded-lg text-base">
          DO_NO_VISIT: insufficient information for a field visit. This record remains in People totals but cannot be scheduled.
        </p>
      )}
      {person.caseStatus && <p className="mb-4 p-3 bg-neutral-100 rounded-lg text-base">
        Case status: {person.caseStatus.replaceAll('_', ' ')}
      </p>}

      <div className="flex flex-wrap gap-2 mb-6">
        <button className="btn btn-primary" onClick={onCall} disabled={!person.phone}>
          Call
        </button>
        <button className="btn btn-secondary" onClick={onLogCall}>Log call</button>
        {!person.doNotVisit && <button className="btn btn-secondary" onClick={onSchedule}>Schedule</button>}
        <button className="btn btn-secondary" onClick={onNote}>Note</button>
        <button className="btn btn-secondary" onClick={onSubmitAddress}>Submit address to AV</button>
        {person.caseRecordId && onReassign && <button className="btn btn-secondary"
          onClick={onReassign}>Reassign case</button>}
      </div>

      <section className="mb-6">
        <h6 className="text-accent-700 mb-2">Location</h6>
        <p className="text-base">{person.addressLine}</p>
        <p className="text-base text-neutral-700">{person.cityLine}</p>

        {/* §19: a shared stop is routed once; the operator needs to know others
            are here so an outcome can be applied to them. */}
        {person.sharedLocationCount > 0 && (
          <p className="text-base text-neutral-700 mt-1">
            {person.sharedLocationCount} other{' '}
            {person.sharedLocationCount === 1 ? 'record' : 'records'} at this address
          </p>
        )}

        {/* §20: unresolved stays visible and is marked, never silently dropped. */}
        {person.unresolvedWarning && (
          <p className="mt-2 p-3 bg-alert-100 border border-alert-300 rounded-lg text-base">
            {person.unresolvedWarning}
          </p>
        )}
      </section>

      <section className="mb-6">
        <h6 className="text-accent-700 mb-2">Next visit</h6>
        <p className="text-base">
          {person.nextVisitLabel ?? 'No visit scheduled'}
          {person.teamName && ` · ${person.teamName}`}
        </p>
      </section>

      <section>
        <h6 className="text-accent-700 mb-2">Recent activity</h6>
        {person.activity.length === 0 ? (
          <p className="text-base text-neutral-700">No activity yet.</p>
        ) : (
          <ul>
            {person.activity.map((a) => (
              <li key={a.id} className="border-b border-neutral-300 py-3">
                <p className="text-base">{a.label}</p>
                <p className="text-sm text-neutral-700">{a.whenLabel}</p>
              </li>
            ))}
          </ul>
        )}
      </section>
      {person.statusHistory.length > 0 && <section className="mt-6">
        <h6 className="text-accent-700 mb-2">Case status history</h6>
        <ul>{person.statusHistory.map((item, index) => <li key={`${item.at}-${index}`}
          className="border-b border-neutral-300 py-2 text-sm">
          {item.previous ? `${item.previous.replaceAll('_', ' ')} → ` : ''}
          {item.next.replaceAll('_', ' ')} · {new Date(item.at).toLocaleString()}
        </li>)}</ul>
      </section>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Call Log sheet (§13)
// ---------------------------------------------------------------------------

/** §13: exactly these outcomes. */
export const CALL_OUTCOMES = [
  { id: 'reached', label: 'Reached' },
  { id: 'no_answer', label: 'No Answer' },
  { id: 'voicemail', label: 'Voicemail' },
  { id: 'wrong_number', label: 'Wrong Number' },
  { id: 'call_back', label: 'Call Back' },
  { id: 'other', label: 'Other' },
] as const;

export type CallOutcomeId = (typeof CALL_OUTCOMES)[number]['id'];

/**
 * Outcomes that automatically arm a follow-up. §13
 * "Reached" and "Wrong Number" are terminal for the call; the rest leave the
 * operator with something outstanding, so the toggle defaults on.
 */
const AUTO_FOLLOW_UP: ReadonlySet<CallOutcomeId> =
  new Set<CallOutcomeId>(['no_answer', 'voicemail', 'call_back']);

export interface CallLogState {
  outcome: CallOutcomeId | null;
  notes: string;
  followUp: boolean;
  followUpDate: string;
  scheduleVisit: boolean;
}

export function callLogDefaults(): CallLogState {
  return { outcome: null, notes: '', followUp: false, followUpDate: '', scheduleVisit: false };
}

/** Applying an outcome may arm the follow-up, but never disarms a manual one. §13 */
export function applyCallOutcome(state: CallLogState, outcome: CallOutcomeId): CallLogState {
  return {
    ...state,
    outcome,
    followUp: AUTO_FOLLOW_UP.has(outcome) ? true : state.followUp,
  };
}

export function CallLogBody({
  state, onChange,
}: { state: CallLogState; onChange: (s: CallLogState) => void }) {
  return (
    <div>
      <fieldset className="mb-4">
        <legend className="text-h6 uppercase text-neutral-700 mb-2">Outcome</legend>
        <div className="flex flex-wrap gap-2">
          {CALL_OUTCOMES.map((o) => (
            <button
              key={o.id}
              onClick={() => onChange(applyCallOutcome(state, o.id))}
              aria-pressed={state.outcome === o.id}
              className={`seg ${state.outcome === o.id ? 'seg-active' : ''}`}
            >
              {o.label}
            </button>
          ))}
        </div>
      </fieldset>

      <label className="field">
        <span>Notes</span>
        <textarea
          className="input min-h-[88px]"
          value={state.notes}
          onChange={(e) => onChange({ ...state, notes: e.target.value })}
        />
      </label>

      <label className="flex items-center gap-3 py-3 min-h-touch cursor-pointer">
        <input
          type="checkbox"
          checked={state.followUp}
          onChange={(e) => onChange({ ...state, followUp: e.target.checked })}
          className="w-5 h-5 accent-accent"
        />
        <span className="text-base">Follow up</span>
      </label>

      {state.followUp && (
        <label className="field">
          <span>Follow-up date</span>
          <input
            type="date"
            className="input"
            value={state.followUpDate}
            onChange={(e) => onChange({ ...state, followUpDate: e.target.value })}
          />
        </label>
      )}

      <label className="flex items-center gap-3 py-3 min-h-touch cursor-pointer">
        <input
          type="checkbox"
          checked={state.scheduleVisit}
          onChange={(e) => onChange({ ...state, scheduleVisit: e.target.checked })}
          className="w-5 h-5 accent-accent"
        />
        <span className="text-base">Schedule a visit</span>
      </label>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Schedule Visit sheet (§14)
// ---------------------------------------------------------------------------

/** §14: allowed durations are exactly these. */
export const VISIT_DURATIONS = [15, 30, 45] as const;
export type VisitDuration = (typeof VISIT_DURATIONS)[number];

export const VISIT_PRIORITIES = [
  { id: 'normal', label: 'Normal' },
  { id: 'high', label: 'High' },
  { id: 'urgent', label: 'Urgent' },
] as const;

export type VisitPriority = (typeof VISIT_PRIORITIES)[number]['id'];

export interface ScheduleVisitState {
  date: string;
  preferredTime: string;
  duration: VisitDuration;
  /** 'auto' means Auto assign. §14 */
  teamId: string;
  priority: VisitPriority;
  notes: string;
}

export function scheduleVisitDefaults(date: string): ScheduleVisitState {
  return {
    date,
    preferredTime: '',
    duration: 30,
    teamId: 'auto',
    priority: 'normal',
    notes: '',
  };
}

export function ScheduleVisitBody({
  state, teams, onChange,
}: {
  state: ScheduleVisitState;
  /** Active teams from production configuration, beyond Alpha/Bravo/Charlie. §14 */
  teams: Array<{ id: string; name: string }>;
  onChange: (s: ScheduleVisitState) => void;
}) {
  return (
    <div>
      <label className="field">
        <span>Date</span>
        <input
          type="date" className="input" value={state.date}
          onChange={(e) => onChange({ ...state, date: e.target.value })}
        />
      </label>

      <label className="field">
        <span>Preferred time</span>
        <input
          type="time" className="input" value={state.preferredTime}
          onChange={(e) => onChange({ ...state, preferredTime: e.target.value })}
        />
      </label>

      <fieldset className="mb-3">
        <legend className="text-h6 uppercase text-neutral-700 mb-2">Duration</legend>
        <div className="flex gap-2">
          {VISIT_DURATIONS.map((d) => (
            <button
              key={d}
              onClick={() => onChange({ ...state, duration: d })}
              aria-pressed={state.duration === d}
              className={`seg ${state.duration === d ? 'seg-active' : ''}`}
            >
              {d} min
            </button>
          ))}
        </div>
      </fieldset>

      <label className="field">
        <span>Team</span>
        <select
          className="input"
          value={state.teamId}
          onChange={(e) => onChange({ ...state, teamId: e.target.value })}
        >
          <option value="auto">Auto assign</option>
          {teams.map((t) => (
            <option key={t.id} value={t.id}>{t.name}</option>
          ))}
        </select>
      </label>

      <fieldset className="mb-3">
        <legend className="text-h6 uppercase text-neutral-700 mb-2">Priority</legend>
        <div className="flex gap-2">
          {VISIT_PRIORITIES.map((p) => (
            <button
              key={p.id}
              onClick={() => onChange({ ...state, priority: p.id })}
              aria-pressed={state.priority === p.id}
              className={`seg ${state.priority === p.id ? 'seg-active' : ''}`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </fieldset>

      <label className="field">
        <span>Notes</span>
        <textarea
          className="input min-h-[72px]"
          value={state.notes}
          onChange={(e) => onChange({ ...state, notes: e.target.value })}
        />
      </label>
    </div>
  );
}

/** Small hook so the two sheets keep their state without the root growing further. */
export function useSheetState<T>(initial: () => T) {
  const [state, setState] = useState<T>(initial);
  return { state, setState, reset: () => setState(initial()) };
}
